import { useEffect, useRef } from 'react';
import { Badge, Chip, Skeleton } from '../../engine/design/primitives/Misc';
import { Icon } from '../../engine/design/icons';
import { priorityTone, statusTone, usePriorities, useStatuses } from './lib/catalog';
import { WORKING_SET_MODES, type WorkingSet, type WorkingSetMode } from './lib/workspace';

// The workspace's queue rail (proposal P1): the working set beside the ticket, so a technician
// never loses the queue to open a ticket. `j`/`k` move through it (registered by the workspace,
// not here); clicking a row opens it. The active row is scrolled into view.

export interface QueueRailProps {
  set: WorkingSet;
  currentId: string;
  mode: WorkingSetMode;
  onModeChange: (mode: WorkingSetMode) => void;
  onOpen: (id: string) => void;
}

export function QueueRail({ set, currentId, mode, onModeChange, onOpen }: QueueRailProps) {
  const statuses = useStatuses();
  const priorities = usePriorities();
  const activeRef = useRef<HTMLButtonElement>(null);
  const position = set.indexOf(currentId);

  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest' });
  }, [currentId]);

  return (
    <nav className="hf-stack" style={{ gap: 'var(--s-2)', height: '100%', minHeight: 0 }} aria-label="Working set">
      <div className="hf-chiprow" role="tablist" aria-label="Working set filter">
        {WORKING_SET_MODES.map((m) => (
          <Chip key={m.key} role="tab" aria-selected={mode === m.key} active={mode === m.key} onClick={() => onModeChange(m.key)}>
            {m.label}
          </Chip>
        ))}
      </div>
      <div className="hf-muted" style={{ fontSize: 'var(--t-11)' }}>
        {set.isLoading ? 'Loading…' : position >= 0 ? `${position + 1} of ${set.rows.length}` : `${set.rows.length} open`}
        <span className="hf-grow" />
      </div>
      {set.isLoading ? (
        <Skeleton lines={6} />
      ) : set.rows.length === 0 ? (
        <p className="hf-muted" style={{ fontSize: 'var(--t-12)' }}>Nothing open here. Press <kbd>g</kbd> then <kbd>a</kbd> for all tickets.</p>
      ) : (
        <ol className="hf-stack" style={{ gap: 2, listStyle: 'none', padding: 0, margin: 0, overflowY: 'auto', minHeight: 0 }}>
          {set.rows.map((r) => {
            const active = r.id === currentId;
            const st = r.status_id ? statuses.byId.get(r.status_id) : undefined;
            const pr = r.priority_id ? priorities.byId.get(r.priority_id) : undefined;
            return (
              <li key={r.id}>
                <button
                  type="button"
                  ref={active ? activeRef : undefined}
                  aria-current={active ? 'true' : undefined}
                  className={['hf-rail-item', active ? 'active' : ''].join(' ').trim()}
                  onClick={() => onOpen(r.id)}
                >
                  <span className="hf-row" style={{ gap: 'var(--s-1)', fontSize: 'var(--t-11)' }}>
                    <span className="hf-mono hf-muted">{r.ref ?? '—'}</span>
                    {pr && <Badge tone={priorityTone(pr.level)} dot>{pr.key.toUpperCase()}</Badge>}
                    <span className="hf-grow" />
                    {st && <Badge tone={statusTone(st.category)} dot={false}>{st.name}</Badge>}
                  </span>
                  <span style={{ display: 'block', textAlign: 'left', fontSize: 'var(--t-12)' }}>{r.summary ?? '(no summary)'}</span>
                </button>
              </li>
            );
          })}
        </ol>
      )}
      <p className="hf-muted" style={{ fontSize: 'var(--t-11)', margin: 0 }}>
        <Icon name="keyboard" size={12} /> <kbd>j</kbd>/<kbd>k</kbd> move · <kbd>?</kbd> all shortcuts
      </p>
    </nav>
  );
}
